import React, { useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {Link} from "react-router-dom";
import CareerProgramTicker from "../CareerProgramTicker";
import {
  ArrowDown,
  ArrowUpRight,
  BookOpenCheck,
  CalendarDays,
  Check,
  Code2,
  Database,
  Globe2,
  Layers3,
  MonitorPlay,
  Sparkles,
  UsersRound,
  Workflow,
  Zap,
} from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/9159042/pexels-photo-9159042.jpeg?auto=compress&cs=tinysrgb&w=2200";
const PLATFORM_IMAGE =
  "https://images.pexels.com/photos/12903341/pexels-photo-12903341.jpeg?auto=compress&cs=tinysrgb&w=1400";

const COLORS = {
  ink: "#071121",
  inkSoft: "#163362",
  blue: "#2D68C4",
  cyan: "#2CC3BC",
  accent: "#0D5EF4",
  cloud: "#F7FAFC",
  line: "rgba(22, 51, 98, 0.14)",
};

const ADVANTAGES = [
  {
    icon: BookOpenCheck,
    title: "24 Weeks of Immersive Training",
    description: "Deep dive into Infor modules (CloudSuite Industrial, Financials, HCM, or Supply Chain).",
  },
  {
    icon: Globe2,
    title: "Placement Assistance",
    description: "Leverage our global staffing network. We place talent in top Fortune 500 companies daily.",
  },
  {
    icon: MonitorPlay,
    title: "Flexible Learning",
    description: "Choose Online (Instructor-Led) or Classroom training at our state-of-the-art facilities.",
  },
  {
    icon: UsersRound,
    title: "Corporate & Individual Tracks",
    description: "Tailored curriculum for freshers, experienced IT professionals, and corporate teams.",
  },
];

const PROGRAMS = [
  {
    id: "infor-cloudsuite-foundations",
    title: "Infor CloudSuite Foundations",
    description:
      "Build a strong foundation in Infor CloudSuite, enterprise processes, and the consulting mindset needed for delivery work.",
    mode: "Online / On-site",
    cadence: "6–8 weeks",
    icon: Layers3,
    image:
      "https://images.pexels.com/photos/9159076/pexels-photo-9159076.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Students focused on laptops in a classroom, photographed by Mikhail Nilov on Pexels",
    featured: true,
  },
  {
    id: "infor-ln-m3",
    title: "Infor LN & M3 ERP",
    description:
      "Learn the core concepts behind Infor LN and Infor M3, from business flows to the way manufacturing teams use ERP.",
    mode: "Online",
    cadence: "6–8 weeks",
    icon: Workflow,
    image:
      "https://images.pexels.com/photos/3182779/pexels-photo-3182779.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "A diverse team collaborating around a laptop, photographed by fauxels on Pexels",
  },
  {
    id: "infor-csi-manufacturing",
    title: "Infor CSI & Smart Manufacturing",
    description:
      "Explore Infor CloudSuite Industrial and the connected processes that help manufacturers plan, produce, and improve.",
    mode: "Online / On-site",
    cadence: "6–8 weeks",
    icon: Database,
    image:
      "https://images.unsplash.com/photo-1572021335469-31706a17aaef?auto=format&fit=crop&w=1200&q=85",
    alt: "Technology team collaborating around a laptop, photographed by Jud Mackrill on Unsplash",
  },
  {
    id: "infor-wms-os",
    title: "Infor WMS & Infor OS",
    description:
      "Develop practical knowledge of warehouse operations, Infor WMS, Infor OS, and the integrations behind connected work.",
    mode: "Online",
    cadence: "6–8 weeks",
    icon: Code2,
    image:
      "https://images.pexels.com/photos/33433724/pexels-photo-33433724.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Modern software engineering workspace with code screens, photographed by Jakub Zerdzicki on Pexels",
  },
];

const PLATFORM_FEATURES = [
  "Access lesson recordings, resources, and assignments in one place.",
  "Track your progress with clear milestones and practical checkpoints.",
  "Keep up with mentor updates, cohort announcements, and next steps.",
  "Build a reusable library of templates, examples, and reference material.",
];

const getRevealProps = (shouldReduceMotion, delay = 0) => ({
  initial: shouldReduceMotion ? false : { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.18 },
  transition: shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.65, delay, ease: [0.16, 1, 0.3, 1] },
});

const SkillAccelerator = () => {
  const shouldReduceMotion = useReducedMotion();

  const scrollToPrograms = useCallback(() => {
    document.getElementById("accelerator-programs")?.scrollIntoView({
      behavior: shouldReduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [shouldReduceMotion]);

  return (
    <div className="overflow-x-clip font-jost" style={{ color: COLORS.inkSoft }}>
      <CareerProgramTicker />

      <section
        className="relative isolate overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(7, 17, 33, 0.94) 0%, rgba(7, 17, 33, 0.82) 42%, rgba(7, 17, 33, 0.32) 100%), url("${HERO_IMAGE}")`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="absolute -right-24 top-24 -z-10 h-72 w-72 rounded-full blur-3xl" style={{ backgroundColor: "rgba(44,195,188,0.16)" }} />
        <div className="mx-auto flex max-w-7xl flex-col px-4 py-6 sm:px-8 sm:py-8 lg:px-12">

          <div className="flex flex-1 items-center py-12 sm:py-20">
            <motion.div {...getRevealProps(shouldReduceMotion)} className="max-w-5xl">
              <h1 className="max-w-7xl text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-5xl">
                Master Infor CloudSuite. Get Placed. <br/>Transform Your Career with KCS Tech.
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-7 text-white sm:mt-8 sm:text-xl sm:leading-8" style={{ textAlign: "left" }}>
                Global IT Staffing Leader KCS Tech brings you an exclusive 24-Week Intensive Certification &amp; Placement Program for Infor CloudSuite.
              </p>
              <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  to="/career/infor-cloud-suite-bootcamp/apply"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-bold text-white transition-colors hover:brightness-110 sm:w-auto"
                  style={{ backgroundColor: COLORS.blue }}
                >
                  Apply for the Next Cohort <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
                {/* <a
                  href="/infor-cloudsuite-syllabus.html"
                  download="Infor-CloudSuite-Program-Syllabus.html"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-white/70 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 sm:w-auto"
                >
                  Download Syllabus <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
                </a> */}
              </div>
            </motion.div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/15 pt-4 text-[10px] font-medium uppercase tracking-[0.12em] text-white sm:gap-x-8 sm:gap-y-3 sm:text-sm sm:tracking-[0.14em]">
            <span>Practical projects</span>
            <span className="h-1 w-1 rounded-full" style={{ backgroundColor: COLORS.cyan }} />
            <span>Industry mentors</span>
            {/* <span className="h-1 w-1 rounded-full" style={{ backgroundColor: COLORS.cyan }} />
            <span>PT KCS community</span> */}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-8 sm:py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <motion.div {...getRevealProps(shouldReduceMotion)} className="mx-auto max-w-none text-center">
            <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: COLORS.blue }}>
              The Opportunity
            </span>
            <h2 className="mt-3 text-[2rem] font-semibold leading-tight tracking-[-0.04em] sm:mt-4 sm:text-5xl" style={{ color: COLORS.ink }}>
              Infor CloudSuite is the backbone of modern enterprise operations.
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-base leading-7 sm:mt-5 sm:text-xl sm:leading-8" style={{ color: COLORS.inkSoft, textAlign: "center" }}>
              With Kapil Tech&apos;s global footprint spanning the <strong>USA, India, and Indonesia</strong>, we don&apos;t just teach you the software—we connect you to the companies that use it.
            </p>
          </motion.div>

          <h3 className="mt-12 text-2xl font-semibold sm:mt-16 sm:text-3xl" style={{ color: COLORS.ink }}>
            Why Choose This Program?
          </h3>
          <div className="mt-6 grid gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {ADVANTAGES.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <motion.article
                  key={advantage.title}
                  {...getRevealProps(shouldReduceMotion, index * 0.06)}
                  className="rounded-md border p-5 transition-transform duration-300 hover:-translate-y-1 sm:p-6"
                  style={{ borderColor: COLORS.line, backgroundColor: index % 2 === 0 ? COLORS.cloud : "#ffffff" }}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-md sm:h-12 sm:w-12" style={{ backgroundColor: COLORS.accent, color: "#ffffff" }}>
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold sm:mt-6 sm:text-xl" style={{ color: COLORS.ink }}>{advantage.title}</h3>
                  <p className="mt-2 text-sm leading-6 sm:mt-3 sm:text-lg sm:leading-7" style={{ color: COLORS.inkSoft, textAlign: "left" }}>{advantage.description}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-8 sm:py-16 lg:px-12" style={{ backgroundColor: COLORS.ink }}>
        <motion.div {...getRevealProps(shouldReduceMotion)} className="mx-auto max-w-7xl text-white">
          <h2 className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: COLORS.cyan }}>The Investment</h2>
          <p className="mt-3 text-4xl text-white font-semibold sm:text-5xl">IDR 47,000,000</p>
          <p className="mt-3 max-w-3xl text-base leading-7 text-white/85 sm:text-lg">
            All-inclusive: Training material, Lab access, Certification prep, and Placement support.
          </p>
        </motion.div>
      </section>

      <section id="accelerator-programs" className="scroll-mt-20 px-4 py-14 sm:px-8 sm:py-24 lg:px-12" style={{ backgroundColor: COLORS.cloud }}>
        <div className="mx-auto max-w-6xl">
          <motion.div {...getRevealProps(shouldReduceMotion)} className="flex flex-col justify-between gap-5 md:flex-row md:items-end sm:gap-6">
            <div className="max-w-3xl">
              <span className="text-xs font-bold uppercase tracking-[0.16em] sm:text-sm sm:tracking-[0.18em]" style={{ color: COLORS.blue }}>
                Choose your next edge
              </span>
              <h2 className="mt-3 text-[2rem] font-semibold leading-tight tracking-[-0.04em] sm:mt-4 sm:text-5xl" style={{ color: COLORS.ink }}>
                Build the Infor expertise modern operations depend on.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-6 sm:text-lg sm:leading-7" style={{ color: COLORS.inkSoft, textAlign: "left" }}>
              Choose one focused Infor path. Leave with knowledge you can explain, demonstrate, and build on.
            </p>
          </motion.div>

          <div className="mt-9 grid gap-4 sm:mt-12 sm:gap-5 lg:grid-cols-2">
            {PROGRAMS.map((program, index) => {
              const ProgramIcon = program.icon;
              return (
                <motion.article
                  key={program.id}
                  {...getRevealProps(shouldReduceMotion, index * 0.05)}
                  className={`group flex flex-col overflow-hidden rounded-[1.25rem] border bg-white shadow-[0_18px_55px_-32px_rgba(7,17,33,0.5)] sm:rounded-[1.75rem] ${program.featured ? "lg:col-span-2 lg:grid lg:grid-cols-[1.05fr_0.95fr]" : ""}`}
                  style={{ borderColor: COLORS.line }}
                >
                  <div className={`relative overflow-hidden ${program.featured ? "h-52 sm:h-64 lg:h-full" : "h-44 sm:h-52"}`}>
                    <img
                      src={program.image}
                      alt={program.alt}
                      loading={index === 0 ? "eager" : "lazy"}
                      width="1200"
                      height="800"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      className="transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#071121]/65 via-transparent to-transparent" />
                    {program.featured && (
                      <span className="absolute left-4 top-4 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] sm:left-5 sm:top-5 sm:px-3 sm:py-1.5 sm:text-xs sm:tracking-[0.12em]" style={{ backgroundColor: COLORS.accent, color: "#ffffff" }}>
                        Featured track
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5 sm:p-8">
                    <div className="flex items-start justify-between gap-3 sm:gap-4">
                      <div>
                        <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] sm:mb-4 sm:gap-2 sm:text-sm sm:tracking-[0.12em]" style={{ color: COLORS.blue }}>
                          <ProgramIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                          Infor track
                        </div>
                        <h3 className="text-xl font-bold leading-tight tracking-[-0.035em] sm:text-3xl" style={{ color: COLORS.ink }}>{program.title}</h3>
                      </div>
                      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 sm:h-5 sm:w-5" style={{ color: COLORS.cyan }} aria-hidden="true" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5 sm:mt-5 sm:gap-2">
                      {[program.mode, program.cadence].map((label) => (
                        <span key={label} className="rounded-full border px-2.5 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm" style={{ borderColor: COLORS.line, color: COLORS.inkSoft }}>
                          {label}
                        </span>
                      ))}
                    </div>
                    <p className="mt-4 text-base leading-7 sm:mt-5 sm:text-xl sm:leading-8" style={{ color: COLORS.inkSoft, textAlign: "left" }}>{program.description}</p>
                    <div className="mt-6 flex flex-1 items-end sm:mt-8">
                      <Link
                        to="/career/infor-cloud-suite-bootcamp/apply" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:brightness-110 sm:w-auto sm:px-5 sm:py-3 sm:text-base"
                        style={{ backgroundColor: COLORS.blue }}
                      >
                        Talk to our team <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-8 sm:py-28 lg:px-12">
        <div className="mx-auto grid max-w-7xl items-center gap-9 sm:gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <motion.div {...getRevealProps(shouldReduceMotion)} className="order-2 lg:order-1">
            <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: COLORS.blue }}>
              Your learning workspace
            </span>
            <h2 className="mt-3 text-[2rem] font-semibold leading-tight tracking-[-0.04em] sm:mt-4 sm:text-5xl" style={{ color: COLORS.ink }}>
              Keep your Infor momentum after every session.
            </h2>
            <p className="mt-4 text-base leading-7 sm:mt-5 sm:text-xl sm:leading-8" style={{ color: COLORS.inkSoft, textAlign: "left" }}>
              Your cohort experience continues beyond the live class. Use one simple learning workspace to revisit Infor material, complete milestones, and stay connected to the mentors helping you move forward.
            </p>
            <ul className="mt-6 space-y-3 sm:mt-8 sm:space-y-4">
              {PLATFORM_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm leading-6 sm:gap-3 sm:text-lg sm:leading-7" style={{ color: COLORS.inkSoft }}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: COLORS.accent, color: "#ffffff" }}>
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...getRevealProps(shouldReduceMotion, 0.1)} className="order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-[1.5rem] p-1.5 sm:rounded-[2rem] sm:p-2">
              <div className="overflow-hidden rounded-[1.25rem] bg-white sm:rounded-[1.5rem]">
                <img
                  src={PLATFORM_IMAGE}
                  alt="Mentor guiding learners with laptops, photographed by Mizuno K on Pexels"
                  loading="lazy"
                  width="1400"
                  height="1000"
                  style={{ width: "100%", height: "auto", aspectRatio: "1.25 / 1", objectFit: "cover" }}
                />
              </div>
              <div className="absolute bottom-4 left-4 rounded-xl border border-white/70 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm sm:bottom-10 sm:left-10 sm:rounded-2xl sm:px-4 sm:py-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] sm:gap-2 sm:text-xs sm:tracking-[0.14em]" style={{ color: COLORS.blue }}>
                  <MonitorPlay className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                  Learn on your terms
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative isolate my-6 overflow-hidden px-4 py-14 text-white sm:my-10 sm:px-8 sm:py-24 lg:px-12" style={{ backgroundColor: COLORS.ink }}>
        <div className="absolute -left-20 top-0 -z-10 h-56 w-56 rounded-full blur-3xl sm:h-72 sm:w-72" style={{ backgroundColor: "rgba(44,195,188,0.18)" }} />
        <div className="absolute -right-20 bottom-0 -z-10 h-56 w-56 rounded-full blur-3xl sm:h-72 sm:w-72" style={{ backgroundColor: "rgba(255,143,0,0.14)" }} />
        <motion.div {...getRevealProps(shouldReduceMotion)} className="relative mx-auto flex max-w-5xl flex-col items-start justify-between gap-6 sm:gap-8 md:flex-row md:items-center">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: COLORS.cyan }}>Ready for your next chapter?</span>
            <h2 className="mt-3 text-[2rem] font-semibold leading-tight tracking-[-0.04em] text-white sm:mt-4 sm:text-5xl">
              Bring your ambition. We will help you turn it into Infor capability.
            </h2>
          </div>
          <Link
            to="/career/infor-cloud-suite-bootcamp/apply" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex w-full items-center justify-center gap-3 rounded-full px-5 py-3 text-sm font-bold transition-transform duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-6 sm:py-3.5"
            style={{ backgroundColor: COLORS.accent, color: "#ffffff" }}
          >
            Start a conversation <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>  
        </motion.div>
      </section>
    </div>
  );
};

export default SkillAccelerator;
